<script setup>
/* eslint-disable */
import { useGameStore } from '@/store/game'
import { computed, ref } from 'vue'
import { getStatGrade, getStatGradeClass } from '@/utils/chocoboStats'
import { getCircuitName } from '@/constants/raceCircuits'
import { getRegionDisplayName } from '@/constants/regionConstants'
import PersonalBest from './PersonalBest.vue'
import { getChocoboColorClass } from '@/utils/colorUtils'
import { useDarkModeStore } from '@/store/darkMode'
import { applyThemeClasses } from '@/utils/themeColors'

const gameStore = useGameStore()

const chocobosByRegion = computed(() => {
  const grouped = {}
  
  gameStore.chocobos.forEach(chocobo => {
    const owner = gameStore.owners.find(owner => owner.chocoboIds.includes(chocobo.id))
    if (!owner) return

    if (!grouped[owner.homeRegion]) {
      grouped[owner.homeRegion] = []
    }

    const formattedChocobo = {
      ...chocobo,
      personalBests: chocobo.personalBests || {},
      owner,
      isOwnedByPlayer: owner.isPlayer,
      recentForm: chocobo.recentForm || Array(5).fill(null),
      raceCount: chocobo.raceHistory?.length || 0,
      wins: chocobo.raceHistory?.filter(r => r.position === 1).length || 0,
      places: chocobo.raceHistory?.filter(r => r.position === 2).length || 0,
      shows: chocobo.raceHistory?.filter(r => r.position === 3).length || 0,
      totalPrizes: chocobo.raceHistory?.reduce((acc, race) => {
        if (!race || !race.circuit) return acc;
        const raceData = RACE_REWARDS[race.circuit.id]?.[race.position] || 
                        RACE_REWARDS[race.circuit.id]?.participation;
        return acc + (raceData?.money * (race.circuit.rewardMultiplier || 1) || 0);
      }, 0) || 0,
      lastThreeRaces: chocobo.raceHistory?.slice(-3).reverse().map(race => ({
        circuit: race?.circuit ? getCircuitName(race.circuit.id) : 'Unknown',
        position: race?.position || 0,
        prize: race?.circuit ? ((RACE_REWARDS[race.circuit.id]?.[race.position] || 
                RACE_REWARDS[race.circuit.id]?.participation)?.money * 
                (race.circuit.rewardMultiplier || 1) || 0) : 0
      })) || []
    };

    grouped[owner.homeRegion].push(formattedChocobo)
  })

  // Sort chocobos by recent form
  for (const region in grouped) {
    grouped[region].sort((a, b) => {
      const aFormScore = a.recentForm.reduce((acc, pos) => {
        if (pos === 1) return acc + 4
        if (pos === 2) return acc + 3
        if (pos === 3) return acc + 2
        return acc + 1
      }, 0)
      
      const bFormScore = b.recentForm.reduce((acc, pos) => {
        if (pos === 1) return acc + 4
        if (pos === 2) return acc + 3
        if (pos === 3) return acc + 2
        return acc + 1
      }, 0)

      if (aFormScore === 5 && bFormScore === 5) return 0
      if (aFormScore === 5) return 1
      if (bFormScore === 5) return -1

      return bFormScore - aFormScore
    })
  }

  return grouped
})

const formatPersonalBest = (pb) => {
  if (!pb) return '-';
  const minutes = Math.floor(pb / 60);
  const seconds = Math.floor(pb % 60);
  const milliseconds = Math.floor((pb % 1) * 1000);
  return `${minutes}:${seconds.toString().padStart(2, '0')}.${milliseconds.toString().padStart(3, '0')}`;
};

const expandedRegions = ref([])

const toggleRegion = (region) => {
  const index = expandedRegions.value.indexOf(region)
  if (index === -1) {
    expandedRegions.value.push(region)
  } else {
    expandedRegions.value.splice(index, 1)
  }
}

const getFormClass = (position) => {
  if (position === null) return 'bg-gray-100 text-gray-600'
  if (position === 1) return 'bg-yellow-100 text-yellow-800'
  if (position === 2) return 'bg-gray-200 text-gray-800'
  if (position === 3) return 'bg-amber-100 text-amber-800'
  return 'bg-red-50 text-red-800'
}

const RACE_REWARDS = {
  regional: {
    1: { money: 4000, xp: 35 },
    2: { money: 3000, xp: 25 },
    3: { money: 2500, xp: 20 },
    participation: { money: 400, xp: 10 }
  },
  continental: {
    1: { money: 4500, xp: 50 },
    2: { money: 3500, xp: 35 },
    3: { money: 2500, xp: 30 },
    participation: { money: 400, xp: 10 }
  },
  transcontinental: {
    1: { money: 5000, xp: 70 },
    2: { money: 3500, xp: 50 },
    3: { money: 2500, xp: 40 },
    participation: { money: 400, xp: 10 }
  },
  global: {
    1: { money: 10000, xp: 100 },
    2: { money: 5000, xp: 80 },
    3: { money: 3000, xp: 60 },
    participation: { money: 800, xp: 20 }
  },
  saucer: {
    1: { money: 25000, xp: 150 },
    2: { money: 15000, xp: 100 },
    3: { money: 10000, xp: 80 },
    participation: { money: 2000, xp: 40 }
  }
}
</script>

<template>
  <div class="px-4 py-6 sm:px-0">
    <div :class="`rounded-lg shadow-lg ${applyThemeClasses('layout.card')}  p-6 mb-6`">
      <div class="flex justify-between items-center">
        <h1 :class="applyThemeClasses('text.primary')">All Known Chocobos</h1>
        <div class="text-gray-600">
          <span :class="applyThemeClasses('text.primaryLow')">Total Chocobos: {{ gameStore.chocobos.length }}</span>
        </div>
      </div>
    </div>

    <!-- Region Sections -->
    <div class="space-y-8">
      <div 
        v-for="(chocobos, region) in chocobosByRegion" 
        :key="region"
        :class="`rounded-lg shadow-lg ${applyThemeClasses('layout.card')}`"
      >
        <button
          @click="toggleRegion(region)"
          class="w-full p-4 border-b flex justify-between items-center"
        >
          <div
          :class="`w-full rounded-lg shadow-lg ${applyThemeClasses('layout.card')}`"
          >
            <h2 :class="applyThemeClasses('text.primary')">{{ getRegionDisplayName(region) }}</h2>
            <p :class="applyThemeClasses('text.secondary')">{{ chocobos.length }} registered chocobos</p>
          </div>
          <svg
            class="w-5 h-5 text-gray-500 transform transition-transform duration-200"
            :class="{ 'rotate-180': expandedRegions.includes(region) }"
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        <div v-if="expandedRegions.includes(region)" class="p-4">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div 
              v-for="chocobo in chocobos" 
              :key="chocobo.id"
              class="border rounded-lg overflow-hidden hover:shadow-md transition-shadow"
            >
              <div class="flex">
                <!-- Chocobo Info -->
                <div class="flex-1 p-4">
                  <div class="flex items-center gap-3">
                    <div 
                      :class="[getChocoboColorClass(chocobo.color), 'w-10 h-10 rounded-lg']"
                    ></div>
                    <div>
                      <h3 :class="applyThemeClasses('text.secondaryBold')">{{ chocobo.name }}</h3>
                      <p :class="applyThemeClasses('text.secondary')">
                        {{ chocobo.owner.ranchName }}
                      </p>
                      <p :class="applyThemeClasses('text.secondary')" class="text-xs">
                        Age: {{ chocobo.getFormattedAge ? chocobo.getFormattedAge(gameStore.currentDay) : 'Unknown' }}
                      </p>
                    </div>
                  </div>

                  <div class="mt-2 grid grid-cols-2 gap-2">
                    <span 
                      :class="[
                        getStatGradeClass(getStatGrade(chocobo.speed)),
                        'px-2 py-1 rounded text-sm font-medium'
                      ]"
                    >
                      Speed: {{ getStatGrade(chocobo.speed) }}
                    </span>
                    <span 
                      :class="[
                        getStatGradeClass(getStatGrade(chocobo.stamina)),
                        'px-2 py-1 rounded text-sm font-medium'
                      ]"
                    >
                      Stamina: {{ getStatGrade(chocobo.stamina) }}
                    </span>
                    <div class="text-sm text-green-600">
                      <span :class="applyThemeClasses('text.money')">Recent Winnings: {{ chocobo.totalPrizes.toLocaleString() }}G</span>
                    </div>
                  </div>
                </div>

                <!-- Form Guide and Times -->
                <div class="flex flex-col w-32 p-4 border-l bg-gray-50">
                  <!-- Race Form -->
                  <div>
                    <div class="text-xs text-gray-600 mb-1">Recent Form</div>
                    <div class="flex gap-1">
                      <span 
                        v-for="(result, index) in chocobo.recentForm"
                        :key="index"
                        :class="[
                          getFormClass(result),
                          'w-5 h-5 flex items-center justify-center rounded text-xs font-medium'
                        ]"
                      >
                        {{ result === null ? '-' : result }}
                      </span>
                    </div>
                  </div>

                  <!-- Personal bests -->
                  <div class="mt-3 pt-3 border-t border-gray-200">
                    <div class="text-xs text-gray-600 mb-1">Best Times:</div>
                    <div class="space-y-1">
                      <div v-if="!chocobo.personalBests || Object.keys(chocobo.personalBests).length === 0" 
                        class="text-xs text-gray-500">
                        <div class="flex justify-between items-center text-xs"><span class="text-gray-500">800m:</span><span class="font-medium text-gray-400">-</span></div>
                        <div class="flex justify-between items-center text-xs"><span class="text-gray-500">2000m:</span><span class="font-medium text-gray-400">-</span></div>
                        <div class="flex justify-between items-center text-xs"><span class="text-gray-500">5000m:</span><span class="font-medium text-gray-400">-</span></div>
                      </div>
                      <template v-else>
                        <div 
                          v-for="(label, distance) in {
                            'short': '800m',
                            'medium': '2000m',
                            'long': '5000m'
                          }"
                          :key="label"
                          class="flex justify-between items-center text-xs"
                        >
                          <span class="text-gray-500">{{ label }}:</span>
                          <span 
                            :class="[
                              'font-medium',
                              chocobo.personalBests[distance] ? 'text-blue-600' : 'text-gray-400'
                            ]"
                          >
                            {{ formatPersonalBest(chocobo.personalBests[distance]) }}
                          </span>
                        </div>
                      </template>
                    </div>
                  </div>

                  <!-- Race stats -->
                  <div class="mt-3 pt-3 border-t border-gray-200 text-xs">
                    <div class="flex justify-between text-gray-600">
                      <span>Races:</span>
                      <span>{{ chocobo.raceCount }}</span>
                    </div>
                    <div class="flex justify-between text-yellow-600">
                      <span>Wins:</span>
                      <span>{{ chocobo.wins }}</span>
                    </div>
                    <div class="flex justify-between text-gray-600">
                      <span>Places:</span>
                      <span>{{ chocobo.places }}</span>
                    </div>
                    <div class="flex justify-between text-amber-600">
                      <span>Shows:</span>
                      <span>{{ chocobo.shows }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>